// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Title, Text, Button, Badge } from '@/components/ui/core';
import { PasswordInput } from '@/components/ui/inputs';
import { IconLock, IconArrowLeft, IconLockCheck, Icon3dCubeSphere, IconCheck } from '@tabler/icons-react';
import { FadeIn } from '@/components/animations';
import { Floating3DCard, PasswordStrength } from '../components';

export default function ResetMinimal3D() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isReset, setIsReset] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsReset(true);
  };

  const passwordsMatch = password === confirmPassword;
  const canSubmit = password.length >= 8 && passwordsMatch;

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark
          ? `linear-gradient(180deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
          : `linear-gradient(180deg, ${theme.colors.gray[0]} 0%, ${theme.colors.gray[1]} 100%)`,
        position: 'relative',
      }}
    >
      {/* Subtle pattern */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(${isDark ? theme.colors.dark[6] : theme.colors.gray[3]} 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />

      <Center mih="100vh" p="xl" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <FadeIn delay={100}>
            <Floating3DCard
              maxTilt={10}
              floatingShadow
              glow
              glowColor={theme.colors.green[5]}
              radius="lg"
            >
              {isReset ? (
                <Stack gap="lg" align="center">
                  <Center
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 20,
                      backgroundColor: 'var(--mantine-color-green-light)',
                    }}
                  >
                    <IconCheck size={40} color="var(--mantine-color-green-6)" />
                  </Center>

                  <Stack align="center" gap="xs">
                    <Title order={2} ta="center">Password Reset!</Title>
                    <Text c="dimmed" ta="center" maw={300}>
                      Your password has been successfully reset.
                    </Text>
                  </Stack>

                  <Button
                    component={Link}
                    to="/app/demo/auth-pages/login/minimal3d"
                    fullWidth
                    size="md"
                    radius="md"
                    color="dark"
                  >
                    Continue to login
                  </Button>
                </Stack>
              ) : (
                <Stack gap="lg">
                  <Button
                    component={Link}
                    to="/app/demo/auth-pages/reset-password"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowLeft size={14} />}
                    w="fit-content"
                  >
                    Back to variants
                  </Button>

                  <Stack align="center" gap="xs">
                    <Badge
                      leftSection={<Icon3dCubeSphere size={12} />}
                      variant="gradient"
                      gradient={{ from: 'gray', to: 'dark' }}
                      size="lg"
                    >
                      Minimal 3D
                    </Badge>
                    <Center
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 14,
                        backgroundColor: 'var(--mantine-color-green-light)',
                        marginTop: 'var(--mantine-spacing-sm)',
                      }}
                    >
                      <IconLockCheck size={28} color="var(--mantine-color-green-6)" />
                    </Center>
                    <Title order={2} ta="center" mt="sm">
                      Set new password
                    </Title>
                    <Text c="dimmed" size="sm" ta="center" maw={300}>
                      Your new password must be different from previous passwords.
                    </Text>
                  </Stack>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="md">
                      <div>
                        <PasswordInput
                          label="New password"
                          placeholder="Enter new password"
                          leftSection={<IconLock size={16} />}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          size="md"
                          radius="md"
                          variant="filled"
                        />
                        {password && <PasswordStrength password={password} variant="bar" />}
                      </div>

                      <PasswordInput
                        label="Confirm password"
                        placeholder="Confirm new password"
                        leftSection={<IconLock size={16} />}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        variant="filled"
                        error={confirmPassword && !passwordsMatch ? "Passwords don't match" : undefined}
                      />

                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        radius="md"
                        color="dark"
                        disabled={!canSubmit}
                      >
                        Reset password
                      </Button>
                    </Stack>
                  </form>

                  <Center>
                    <Button
                      component={Link}
                      to="/app/demo/auth-pages/login/minimal3d"
                      variant="subtle"
                      leftSection={<IconArrowLeft size={14} />}
                    >
                      Back to login
                    </Button>
                  </Center>
                </Stack>
              )}
            </Floating3DCard>
          </FadeIn>
        </Container>
      </Center>
    </Box>
  );
}
